import axios from 'axios';
import conf from '../../config';
import { get, set } from 'lodash';
import { getConfig, getKeyMaps } from '../../actions/utils';

const getStore = () => {
  return get(window, 'STORE', { getState: () => {} });
};

const getCurrentToken = () => {
  const store = getStore();
  const sState = store.getState();
  return get(sState, 'user.token', '');
};

export const createInstance = (url) => {
  const config = conf();

  const inst = axios.create({
    baseURL: `${url || config.url}/api`,
  });

  inst.interceptors.request.use(
    config => {
      if (!config.url.includes('/users/token')) {
        if (!get(config, 'headers.Authorization')) {
          set(config, 'headers.Authorization', `Bearer ${getCurrentToken()}`);
        }
      }

      return config;
    },
    error => {
      throw Promise.reject(error);
    }
  );

  let loggingOut = false;

  inst.interceptors.response.use(
    value => {
      return value;
    },
    async err => {
      const status = get(err, 'response.status');
      // const error = new Error(err);
      // Login/verify endpoints handle their own 401s (e.g. MFA required, bad
      // credentials) in the auth UI — don't treat those as an expired session.
      const reqUrl = get(err, 'config.url', '');
      const isAuthEndpoint = reqUrl.includes('/auth/login') || reqUrl.includes('/auth/verify');
      if (status == 401 && !loggingOut && !isAuthEndpoint) {
        loggingOut = true;
        await localStorage.clear();
        alert('Your token is no longer valid. Please login again.');
        window.location.href = window.location.origin;
        setTimeout(() => window.location.reload(), 50);
      }

      throw err;
    }
  );

  return inst;
};

export const loadInstance = (opts = {}) => {
  opts = {
    useExternal: false,
    ...opts,
  }

  const hasExternal = getConfig('externalUrl');

  return createInstance(opts.useExternal && hasExternal ? hasExternal : undefined);
}

export let instance = createInstance();

export const getLicenseNumbers = async (useLocal = true, opts = {}) => {
  opts = {
    useExternal: false,
    ...opts,
  }
  const LNS = getKeyMaps('license');
  if (LNS && Object.keys(LNS).length && useLocal) {
    return Object.keys(LNS).map(k => LNS[k]);
  }
  const resp = await loadInstance(opts).get(`/license/licensenumbers`, { data: {} });
  return get(resp, 'data.data.licensenumbers', []);
};

export const updateSpreadsheet = async () => {
  const resp = await instance.post('/sheet/updateSheet', { data: {} });
  return get(resp, 'data', {});
}

export const getStoredLicenseNumbers = async () => {
  const resp = await instance.get(`/license/lnstored`, { data: {} });
  return get(resp, 'data.data', []);
};

export const updateStoredLicenseNumbers = async (data) => {
  const resp = await instance.post(`/license/updatestored`, data);
  return get(resp, 'data.data', []);
};

export const getMetrcCreds = async () => {
  const resp = await instance.get('/license/metrcCreds');
  return get(resp, 'data.data', {});
};

export const saveMetrcCreds = async (data) => {
  const resp = await instance.post('/license/metrcCreds', data);
  return get(resp, 'data.data', {});
};

export const runLicenseScrape = async () => {
  const resp = await instance.post('/license/runscrape', {});
  return get(resp, 'data.data', {});
};

export const uploadLogo = async ({ data, size, contentType }) => {
  const resp = await instance.post('/uploads/logo', { data, size, contentType });
  return get(resp, 'data.url');
};

export const updateStoredLicenseNumber = async (data) => {
  const stored = await getStoredLicenseNumbers();

  const ln = get(data, 'License #');

  if (ln) {
    // 'ln' was quoted, so this always read undefined and the write REPLACED the stored
    // row instead of merging onto it — every key the caller left out (emails, active,
    // _deleted, the card-286 lock and its pinned values) was dropped or re-authored from
    // whatever the caller happened to be holding. Callers must send only the fields they
    // own; everything else on the row stays as it is.
    const curln = get(stored, ln, {});
    set(stored, ln, { ...curln, ...data });
  
    const resp = await instance.post(`/license/updatestored`, stored);
    getLicenseNumbers(false);
    return get(resp, 'data.data', []);
  }
};